import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import Axios from '@services/axios';
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const { organization, memberships } = useOrganization();
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  const getSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    setSubscriptionError(null);

    while (true) {
      try {
        // Wait for Clerk to be ready and token to be available
        if (!window.Clerk?.session) {
          console.log('Clerk session not available, retrying in 1s...');
          await new Promise((resolve) => setTimeout(resolve, 1000));
          continue;
        }

        const response = await Axios.get('/user/subscription');

        setSubscription(response.data.data);
        setSubscriptionError(null);
        break;
      } catch (error) {
        console.error('Failed to fetch subscription, retrying in 2s...', error);
        setSubscriptionError('Could not fetch subscription. Retrying...');
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    setSubscriptionLoading(false);
  }, []);

  const refetchSubscription = async () => {
    await getSubscription();
  };

  useEffect(() => {
    if (isLoaded && userId) {
      getSubscription();
    } else if (isLoaded && !userId) {
      setSubscriptionLoading(false);
      setSubscription(null);
      setSubscriptionError(null);
    }
  }, [getSubscription, isLoaded, userId]);

  const value = {
    user,
    organization,
    memberships,
    subscription,
    subscriptionLoading,
    subscriptionError,
    refetchSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
